import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useArtist } from '../../contexts/ArtistContext';
import { useUser } from '../../contexts/UserContext';

const ArtistSelector: React.FC = () => {
  const {
    currentArtistId,
    setCurrentArtistId,
    availableArtists,
    loadingArtists,
    loadingTeams,
    isInitialized,
  } = useArtist();
  const { userProfile } = useUser();

  // Show skeleton placeholder while loading (matches dropdown size)
  // Check loadingTeams, loadingArtists, or if context not initialized yet
  // Also show skeleton if artists array is empty but context isn't initialized (still loading)
  const isLoading = loadingTeams || loadingArtists || !isInitialized || (availableArtists.length === 0 && !isInitialized);
  
  if (isLoading) {
    return (
      <div className="relative w-full min-w-0">
        <div className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm font-family-messina-sans w-full min-w-0 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none flex-shrink-0">
          <ChevronDown size={16} className="text-gray-400" />
        </div>
      </div>
    );
  }

  // If no artists after loading is complete, show first + last name
  // This only shows when isInitialized is true and availableArtists is empty
  if (availableArtists.length === 0) {
    const fallbackName = [userProfile?.firstname, userProfile?.lastname].filter(Boolean).join(' ').trim() || 'User';
    return (
      <div className="text-sm font-medium text-gray-900 font-family-messina-modern break-words leading-tight">
        {fallbackName}
      </div>
    );
  }

  if (availableArtists.length === 1) {
    const artist = availableArtists[0];
    return (
      <div className="px-4 py-2 text-sm text-gray-900 font-family-messina-sans">
        {artist.artistName}
        {artist.isOwner && (
          <span className="ml-2 text-xs text-gray-500">(My Account)</span>
        )}
      </div>
    );
  }

  const currentArtist = availableArtists.find(a => a.artistId === currentArtistId);
  const displayText = currentArtist 
    ? `${currentArtist.artistName}${currentArtist.isOwner ? ' (My Account)' : ''}`
    : '';

  // Sort artists: "My Account" (isOwner === true) first, then others
  const sortedArtists = [...availableArtists].sort((a, b) => {
    if (a.isOwner && !b.isOwner) return -1; // a (My Account) comes first
    if (!a.isOwner && b.isOwner) return 1;  // b (My Account) comes first
    return 0; // Keep original order for same type
  });

  return (
    <div className="relative w-full min-w-0" title={displayText}>
      <select
        value={currentArtistId || ''}
        onChange={(e) => {
          const artistId = e.target.value ? parseInt(e.target.value, 10) : null;
          setCurrentArtistId(artistId);
        }}
        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm text-gray-900 font-family-messina-sans focus:outline-none focus:border-gray-900 cursor-pointer w-full min-w-0"
        style={{ 
          maxWidth: '100%',
          overflow: 'visible'
        }}
      >
        {sortedArtists.map((artist) => {
          const optionText = `${artist.artistName}${artist.isOwner ? ' (My Account)' : ''}`;
          return (
            <option key={artist.artistId} value={artist.artistId} title={optionText}>
              {optionText}
            </option>
          );
        })}
      </select>
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none flex-shrink-0">
        <ChevronDown size={16} className="text-gray-600" />
      </div>
    </div>
  );
};

export default ArtistSelector;

